import React, { useEffect } from "react";
import { Flex, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import {
  FileTextOutlined,
  SettingOutlined,
  CheckCircleFilled,
  DatabaseOutlined,
  DollarOutlined,
  ApartmentOutlined,
  PartitionOutlined,
  InboxOutlined,
  SwapOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import SbDivider from "uiCore/SbDivider/SbDivider";
import SbRow from "uiCore/SbRow/SbRow";
import SbCol from "uiCore/SbCol/SbCol";
import SbSpin from "uiCore/SbSpin/SbSpin";
import EnvironmentWrapper from "Components/EnvironmentWrapper/EnvironmentWrapper";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { useHasAccess } from "Routes/hooks/useHasAccess";
import useOrgContext from "./hooks/useOrgContext";
import OrganizationSection from './components/OrganizationSection';
import SetupCard from './components/SetupCard';
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  PageWrapperStyled,
  PageLoaderStyled,
  SectionWrapperStyled,
  SectionHeaderStyled,
  SectionTitleStyled,
} from "./AdminSetup.styles";

const { Title } = Typography;

// PRD §5.16 — tooltip content strings for the Admin Setup dashboard cards
const TOOLTIP_EMPLOYEE_FUNCTION_ACCESS = "Manage your employee roster — used for JD approvals, talent mobility, and PAIC analysis.";
const TOOLTIP_COMPENSATION_MANAGEMENT = "Manage compensation and currency settings used across roles, JDs, and reporting.";


const MODULE_SETTINGS_CARDS = [
  {
    title: "User Level Views",
    desc: "Scope of the organization visible to a user",
    icon: <DatabaseOutlined />,
    badge: "One New Employee",
    badgeColor: "success",
    badgeIcon: <CheckCircleFilled />,
    route: "/admin-setup/employee-function-access",
    tooltip: TOOLTIP_EMPLOYEE_FUNCTION_ACCESS,
  },
  // Ownership Transfer (Ownership design, part 4): HR Admins only, as its route (admin_setup edit)
  {
    title: "Ownership Transfer",
    desc: "Transfer ownership of JDs and People + AI items to a new owner",
    icon: <UserSwitchOutlined />,
    badge: "Transfer",
    badgeColor: "success",
    badgeIcon: <CheckCircleFilled />,
    route: "/admin-setup/ownership-transfer",
    resource: "admin_setup",
    actions: ["edit"],
  },
  // {
  //   title: "Paid Comparison",
  //   desc: "Compare and analyze pay scales",
  //   icon: <FileTextOutlined />,
  //   badge: "2 pending",
  //   badgeColor: "default",
  //   badgeIcon: <CheckCircleFilled />,
  //   route: null,
  // },
  // {
  //   title: "Position",
  //   desc: "Manage organizational positions",
  //   icon: <DatabaseOutlined />,
  //   badge: "View All",
  //   badgeColor: "success",
  //   badgeIcon: <CheckCircleFilled />,
  //   route: null,
  // },
  {
    title: "JD Management",
    desc: "Manage JD templates, About Company & Values & Culture",
    icon: <FileTextOutlined />,
    route: "/admin-setup/jd-management",
  },
  {
    title: "Compensation Management",
    desc: "Salary ranges and per-entity currency & FX settings",
    icon: <DollarOutlined />,
    route: "/admin-setup/compensation",
    tooltip: TOOLTIP_COMPENSATION_MANAGEMENT,
  },
  {
    title: "Talent Mobility Settings",
    desc: "Match & succession thresholds and open-role visibility",
    icon: <SwapOutlined />,
    route: "/admin-setup/talent-mobility-settings",
    resource: "talent_mobility_admin",
    actions: ["view"],
  },
];

// Reusable card component — adapts a card config onto the shared SetupCard
const ModuleCard = ({ card, onNavigate }) => (
  <SetupCard
    icon={card.icon}
    title={card.title}
    description={card.desc}
    badge={card.badge ? { text: card.badge, color: card.badgeColor, icon: card.badgeIcon } : null}
    onClick={() => card.route && onNavigate(card.route)}
  />
);

// Check if user has access to a card based on entitlements
const cardHasAccess = (card, entitlements) => {
  if (!card.resource) return true;
  const match = entitlements?.find((e) => e.name === card.resource);
  const allowed = match?.entitlements ?? [];
  return card.actions?.some((a) => allowed.includes(a) || allowed.includes('*')) ?? false;
};

// Section renderer — only renders if at least one card is accessible
const SectionCards = ({ title, icon, cards, onNavigate, entitlements }) => {
  const visibleCards = cards.filter((card) => cardHasAccess(card, entitlements));
  if (!visibleCards.length) return null;

  return (
    <SectionWrapperStyled>
      <SectionHeaderStyled align="center">
        {icon}
        <SectionTitleStyled>{title}</SectionTitleStyled>
      </SectionHeaderStyled>
      <SbRow gutter={[14, 14]}>
        {visibleCards.map((card) => (
          <SbCol key={card.title} xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
            {card.envOnly ? (
              <EnvironmentWrapper env={card.envOnly}>
                <ModuleCard card={card} onNavigate={onNavigate} />
              </EnvironmentWrapper>
            ) : (
              <ModuleCard card={card} onNavigate={onNavigate} />
            )}
          </SbCol>
        ))}
      </SbRow>
    </SectionWrapperStyled>
  );
};

const ANALYTICS_FROM = "admin_setup";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.ADMIN_SETUP.VIEWED;

const AdminSetup = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const { entitlements } = useEntitlementsStore();
  const { hasAccess } = useHasAccess();
  const { data: orgContextData, loading: orgContextLoading, updateOrgContext } = useOrgContext();

  const ORGANIZATION_ROLE_CARDS = [
    {
      title: "Upload External JD",
      desc: "Import existing JDs into your Jobs Library for AI analysis",
      icon: <FileTextOutlined />,
      badge: "Upload & View",
      badgeColor: "success",
      badgeIcon: <CheckCircleFilled />,
      route: "/admin-setup/upload-external-jd",
      resource: "admin_setup-upload_external_jd",
      actions: ["view", "create"],
    },
    {
      title: "All Job Codes",
      desc: "Create and view job codes for your organization",
      icon: <DatabaseOutlined />,
      badge: "View All",
      badgeColor: "success",
      badgeIcon: <CheckCircleFilled />,
      route: "/admin-setup/job-codes",
    },
    {
      title: "Employee Data",
      desc: "Onboard employees and build the reporting hierarchy",
      icon: <ApartmentOutlined />,
      badge: "Build & View",
      badgeColor: "success",
      badgeIcon: <CheckCircleFilled />,
      route: "/admin-setup/employee-data",
    },
    {
      title: "Role Reporting",
      desc: "Assign reporting managers and mark Apex Roles across job descriptions",
      icon: <PartitionOutlined />,
      badge: "Assign & View",
      badgeColor: "success",
      badgeIcon: <CheckCircleFilled />,
      route: "/admin-setup/role-reporting",
    },
    {
      title: "JD Archive",
      desc: "Archive job descriptions that are no longer active",
      icon: <InboxOutlined />,
      badge: "Archive",
      badgeColor: "success",
      badgeIcon: <CheckCircleFilled />,
      route: "/admin-setup/jd-archive",
    },
  ];

  const SECTIONS = [
    { title: "Organization Role", cards: ORGANIZATION_ROLE_CARDS },
    { title: "Module Settings", cards: MODULE_SETTINGS_CARDS },
  ];

  // Gate on the admin_setup entitlement (same as the route config), not a hardcoded role name
  const canViewAdminSetup = hasAccess('admin_setup', ['view']);

  if (!canViewAdminSetup) {
    return (
      <PageWrapperStyled>
        <Flex align="center" justify="center" style={{ minHeight: '60vh' }}>
          <Typography.Text type="secondary">
            You don't have access to Admin Setup.
          </Typography.Text>
        </Flex>
      </PageWrapperStyled>
    );
  }

  // Show a single page loader until the Admin Setup data has loaded
  if (orgContextLoading) {
    return (
      <PageWrapperStyled>
        <PageLoaderStyled align="center" justify="center">
          <SbSpin size="large" />
        </PageLoaderStyled>
      </PageWrapperStyled>
    );
  }

  const visibleSections = SECTIONS.filter((s) =>
    s.cards.some((card) => cardHasAccess(card, entitlements))
  );

  return (
    <PageWrapperStyled>
      <Flex align="center" gap={8} style={{ marginBottom: 32 }}>
        <Title level={3} style={{ margin: 0 }}>
          Admin Setup
        </Title>
      </Flex>

      {/* Organization Context Section - NEW */}
      <>
        {/* {!orgContextLoading && orgContextData && (
          <SbAssistCard
            completionPercentage={orgContextData?.completion_percentage || 0}
            message={orgContextData?.assist_message}
          />
        )} */}
        
        <OrganizationSection
          cards={orgContextData?.cards || {}}
          onUpdate={updateOrgContext}
          loading={orgContextLoading}
        />
        
        <SbDivider style={{ margin: '32px 0' }} />
      </>

      {/* Existing Sections */}
      {visibleSections.map((section, i) => (
        <React.Fragment key={section.title}>
          <SectionCards
            title={section.title}
            icon={<SettingOutlined />}
            cards={section.cards}
            onNavigate={navigate}
            entitlements={entitlements}
          />
          {i < visibleSections.length - 1 && <SbDivider />}
        </React.Fragment>
      ))}
    </PageWrapperStyled>
  );
};

export default AdminSetup;
